import { z } from 'zod';

import { FileActor } from 'data/common/models';
import { DepositMethod } from 'data/payments/models';

import { DepositRateType } from './DepositRate';

export const DisputeReason = z.enum([
  'REPORT_REQUEST',
  'FAKE_CHECK',
  'INCORRECT_CHECK',
  'INCORRECT_BANK',
  'INCORRECT_AMOUNT',
  'EXPIRED_PAY',
  'REFUND',
  'PROCESSING_CHECK',
  'FUNDS_NOT_RECEIVED',
  'TIMER',
]);
export type DisputeReason = z.infer<typeof DisputeReason>;

export const Comment = z.object({
  actor: FileActor,
  text: z.string(),
  insertedAt: z.coerce.date(),
});
export type Comment = z.infer<typeof Comment>;

export const SortDirection = z.enum(['asc', 'desc']);
export type SortDirection = z.infer<typeof SortDirection>;

export const TraderDepositTab = z.enum([
  'WORKING_CREDENTIALS',
  'CREDENTIALS',
  'AUTOMATION',
  'CREATED',
  'DISPUTED',
  'ARCHIVED',
]);
export type TraderDepositTab = z.infer<typeof TraderDepositTab>;

export const TraderDepositStatus = z.enum([
  'CREATED',
  'PAID',
  'EXPIRED',
  'CANCELLED',
  'DISPUTED',
]);
export type TraderDepositStatus = z.infer<typeof TraderDepositStatus>;

export const TraderDeposit = z.object({
  id: z.string().uuid(),
  publicId: z.string(),
  traderId: z.string().uuid(),
  credentialsId: z.string().uuid(),
  inAmount: z.string(),
  inCurrency: z.string(),
  outAmount: z.string(),
  outCurrency: z.string(),
  outFee: z.string(),
  status: TraderDepositStatus,
  bank: z.string(),
  method: DepositMethod,
  value: z.string(),
  expiryDate: z.coerce.date(),
  credentialTag: z.string().nullish(),
  credentialTagColorId: z.number().nullish(),
  comments: Comment.array(),
  disputeReason: DisputeReason.nullable(),
  finishedByDispute: z.boolean().default(false),
  rateType: DepositRateType,
  disputeExpiresAtMerchant: z.coerce.date().nullable(),
  disputeExpiresAtTrader: z.coerce.date().nullable(),
  statusUpdatedAt: z.coerce.date(),
  insertedAt: z.coerce.date(),
  merchantReplied: z.boolean(),
});
export type TraderDeposit = z.infer<typeof TraderDeposit>;

const TraderDepositSortField = z.enum(['amount', 'statusUpdatedAt']);
export type TraderDepositSortField = z.infer<typeof TraderDepositSortField>;

const TraderDepositSort = z.object({
  sortField: TraderDepositSortField.optional(),
  sortDirection: SortDirection.optional(),
});
export type TraderDepositSort = z.infer<typeof TraderDepositSort>;

export const TraderDepositPaginationFilters = z
  .object({
    status: TraderDepositStatus.array().default([]),
    credentialIds: z.string().array().default([]),
    banks: z.string().array().default([]),
    traderIds: z.string().array().default([]),
    methods: DepositMethod.array().default([]),
    roundAmount: z
      .string()
      .transform((value) => value === 'true')
      .optional(),
    finishedByDispute: z
      .string()
      .transform((value) => value === 'true')
      .optional(),
    minAmount: z.coerce.number().min(0).optional(),
    maxAmount: z.coerce.number().min(0).optional(),
  })
  .merge(TraderDepositSort);
export type TraderDepositPaginationFilters = z.infer<
  typeof TraderDepositPaginationFilters
>;

export const ACTIVE_STATUSES = TraderDepositStatus.options.filter(
  (status) => status === 'CREATED',
);
export const DISPUTED_STATUSES = TraderDepositStatus.options.filter(
  (status) => status === 'DISPUTED',
);
export const ARCHIVED_STATUSES = TraderDepositStatus.options.filter(
  (status) => !['CREATED'].includes(status),
);

export const TraderDepositsCountValue = z.object({
  count: z.number(),
  amount: z.string(),
});
export type TraderDepositsCountValue = z.infer<typeof TraderDepositsCountValue>;

export const TraderDepositsCount = z.record(
  TraderDepositTab,
  TraderDepositsCountValue,
);
export type TraderDepositsCount = z.infer<typeof TraderDepositsCount>;

export const FileType = z.enum(['photo', 'video', 'document']);
export type FileType = z.infer<typeof FileType>;

export const TraderDisputeFile = z.object({
  signedUrl: z.string(),
  filename: z.string(),
  type: FileType,
});
export type TraderDisputeFile = z.infer<typeof TraderDisputeFile>;

export const TraderDisputeFileGroup = z.object({
  actor: FileActor,
  uploadTime: z.coerce.date(),
  files: TraderDisputeFile.array(),
});
export type TraderDisputeFileGroup = z.infer<typeof TraderDisputeFileGroup>;
